import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { SectionHeading } from "../components/Shared";
import { company, images, benefits, serviceCards } from "../mock/mock";

const ucaas = [
  "Decades of implementation experience and a broad portfolio of strategic partners",
  "Unified communications needs analysis",
  "Review of existing services to build an ROI/TCO cost analysis",
  "Supplier and technology evaluation, comparison and demos",
  "Desktop and mobile application & workload integration",
  "Process improvement consulting",
  "Supplier selection, contract negotiation, implementation and support",
  "Ease of transition and training for employees moving to a new system",
  "No disruption in external communications with clients or partners",
];

const ccaas = [
  "Improve efficiency of internal communications",
  "Connect inbound and outbound email, chat, text and social communications",
  "Improve customer service and create one source of data for every client",
  "Empower clients with self-serve FAQs that don't need live agents",
  "Track support agent productivity with advanced reporting",
  "Analyze sales performance and improve sales forecasting",
  "Integrate support ticketing with PBX",
  "Modernize your phone systems and hardware",
  "Advanced data analysis powered by machine learning and AI",
  "Integration of applications across departments and divisions",
];

const WhyUs = () => {
  useEffect(() => {
    document.title = "Why Us - Collaboration & CX Solutions | Morrisville Communications";
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="brand-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-300">Why us?</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold mt-4 max-w-3xl leading-tight">
            Collaboration & CX.
          </h1>
          <p className="mt-5 text-xl text-blue-100 max-w-2xl">
            Enhance communication, improve customer satisfaction, and drive productivity - we help you compare and price collaboration & CX solutions in a fraction of the time.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/contact">
              <Button className="bg-white text-blue-800 hover:bg-blue-50 rounded-full h-12 px-7 font-semibold">
                Sign Up Now <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <a href={`tel:${company.phoneTel}`}>
              <Button variant="outline" className="rounded-full h-12 px-7 font-semibold border-white/40 text-white bg-white/10 hover:bg-white/20">
                <Phone className="h-4 w-4 mr-2" /> {company.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* BENEFITS STRIP */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="No-cost consultation" title="Reliable & experienced engineers" subtitle="Hosted VoIP solutions with a financially backed 99.999% uptime SLA. 3 free months & partner discounts of 15%-40%." />
          <div className="mt-10 grid sm:grid-cols-2 gap-3">
            {benefits.slice(0, 12).map((b, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl p-4 bg-slate-50 border border-slate-100">
                <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <span className="text-slate-700 text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UCaaS / CCaaS */}
      <section className="py-20 brand-gradient-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8">
          <Card className="p-8 rounded-3xl bg-white border-slate-100">
            <h3 className="text-2xl font-bold text-slate-900">Why UCaaS?</h3>
            <p className="mt-2 text-slate-600">Integrate multiple real-time communication channels in a single platform.</p>
            <div className="mt-6 space-y-3">
              {ucaas.map((u, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm">{u}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-8 rounded-3xl bg-white border-slate-100">
            <h3 className="text-2xl font-bold text-slate-900">Why CCaaS?</h3>
            <p className="mt-2 text-slate-600">Improve customer care and increase sales growth with a seamless client experience.</p>
            <div className="mt-6 space-y-3">
              {ccaas.map((c, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm">{c}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>

      {/* SPECIALISTS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden shadow-xl">
            <img src={images.whyus} alt="Unified communications on devices" className="w-full h-[380px] object-cover" />
          </div>
          <div>
            <SectionHeading eyebrow="You are in good hands" title="Meet with our specialists today" />
            <p className="mt-5 text-slate-600 text-lg">
              Morrisville Communications LLC provides top-notch telephone services designed to meet the communication needs of businesses of every size - with call forwarding, voicemail transcription, call routing, excellent support and competitive pricing.
            </p>
            <p className="mt-4 text-slate-600 text-lg">
              Ready to get started? Sign up for our no-cost consulting services today. View the benefits of these solutions and see the top providers we recommend to clients.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button className="brand-gradient text-white rounded-full h-12 px-7 font-semibold">
                  Sign Up Now <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <a href={`mailto:${company.email}`}>
                <Button variant="outline" className="rounded-full h-12 px-7 font-semibold border-slate-300">
                  <Mail className="h-4 w-4 mr-2" /> Email us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE CARDS */}
      <section className="py-20 brand-gradient-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="How we help" title="We compare & price CX solutions in a fraction of the time" />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceCards.map((s) => (
              <Card key={s.title} className="card-hover p-7 rounded-2xl border-slate-100 bg-white">
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyUs;
